import { useState, useMemo, useCallback, useEffect } from 'react';
import { debounce } from '@material-ui/core';
import { searchMembersForFeed } from '../../../../../util/APIUtils';
import { deduplicateSearchMembersAndGroupLocationsHistory, getErrorMsg } from '../../services/Feed.service';
import store, { changeToastType, showToast } from '../../../../core/store';
import {
  getLearners,
  getAttorneys,
  validateAttorneyLearnerSelection,
  isValidSelectionForConversation,
} from './helpers';

// Global styles for autocomplete dropdown (matching NewMessageModal)
const getGlobalFooterStyles = () => `
  .MuiAutocomplete-paper .dropdown-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    display: flex;
    padding: 24px 20px 12px 20px;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
    align-self: stretch;
    z-index: 2;
  }
  
  .MuiAutocomplete-paper .dropdown-footer .footer-left {
    color: var(--success-green-green-dark, #228053);
    leading-trim: both;
    text-edge: cap;
    font-family: Inter;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0.24px;
    margin-right: auto;
  }
  
  .MuiAutocomplete-paper .dropdown-footer .footer-right {
    color: var(--primary-blue-primary-deep, #0F2C54);
    text-align: right;
    leading-trim: both;
    text-edge: cap;
    font-family: Inter;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0.24px;
  }

  /* Scrollbar styling for dropdown paper */
  .MuiAutocomplete-paper::-webkit-scrollbar {
    width: 6px;
    flex-shrink: 0;
  }

  .MuiAutocomplete-paper::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3.5px;
  }

  .MuiAutocomplete-paper::-webkit-scrollbar-thumb {
    background: var(--Greyscale---grey-60, #CDD4E3);
    border-radius: 3.5px;
    flex-shrink: 0;
  }

  .MuiAutocomplete-paper::-webkit-scrollbar-thumb:hover {
    background: #B8C5D6;
  }

  .MuiAutocomplete-paper::-webkit-scrollbar-thumb:active {
    background: #A3B6C9;
  }

  /* Listbox width and layout */
  .MuiAutocomplete-listbox {
    width: 100% !important;
    align-self: stretch !important;
  }

  /* Scrollbar styling for listbox */
  .MuiAutocomplete-listbox::-webkit-scrollbar {
    width: 6px;
    flex-shrink: 0;
  }

  .MuiAutocomplete-listbox::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3.5px;
  }

  .MuiAutocomplete-listbox::-webkit-scrollbar-thumb {
    background: var(--Greyscale---grey-60, #CDD4E3);
    border-radius: 3.5px;
    flex-shrink: 0;
  }

  .MuiAutocomplete-listbox::-webkit-scrollbar-thumb:hover {
    background: #B8C5D6;
  }

  .MuiAutocomplete-listbox::-webkit-scrollbar-thumb:active {
    background: #A3B6C9;
  }

  /* Option padding */
  .MuiAutocomplete-listbox .MuiAutocomplete-option {
    padding-top: 10px !important;
    padding-bottom: 6px !important;
  }

  /*
   * Hover/selected highlight for AutocompleteOption rows.
   * Shared AutocompleteOption no longer owns JS hover handlers; MemberAutocomplete
   * injects the same rule via its footer styles — AttorneyModal must too.
   */
  .MuiAutocomplete-listbox .MuiAutocomplete-option:hover:not([aria-disabled="true"]),
  .MuiAutocomplete-listbox .MuiAutocomplete-option[data-focus="true"]:not([aria-disabled="true"]),
  .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused:not([aria-disabled="true"]),
  .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"]:not([aria-disabled="true"]) {
    background-color: #F3E8FF !important;
  }
`;

const SEARCH_ITEM_DELAY = 500;

export const useAttorneyModalHook = ({
  onCreateConversation,
  maximumMembersAllowed,
  initialConversationSettings = {},
  isCreateLoading = false,
}) => {
  // State for searched members (separate from NewMessageModal)
  const [searchedMembers, setSearchedMembers] = useState([]);
  const [isMemberSearchLoading, setIsMemberSearchLoading] = useState(false);

  // Debounced search function - always searches for PERSON type with forAttorney flag
  const searchItem = useMemo(
    () =>
      debounce(async (value) => {
        setIsMemberSearchLoading(true);
        try {
          const memberFeeds = await searchMembersForFeed(value, 'PERSON', true);
          memberFeeds.data = deduplicateSearchMembersAndGroupLocationsHistory(memberFeeds);
          setIsMemberSearchLoading(false);
          if (memberFeeds.status === 200) {
            setSearchedMembers(memberFeeds.data);
            return;
          }
          store.dispatch(changeToastType("error"));
          store.dispatch(showToast(getErrorMsg(memberFeeds.status)));
        } catch (error) {
          console.error(error);
          setIsMemberSearchLoading(false);
          store.dispatch(changeToastType("error"));
          store.dispatch(showToast("Something went wrong while searching members"));
        }
      }, SEARCH_ITEM_DELAY),
    []
  );

  // Filter and map memberOptions to IMemberOption structure (type === 'person')
  const memberOptions = useMemo(() => {
    if (!searchedMembers || searchedMembers.length === 0) {
      return [];
    }
    
    // Filter and map to only include person/member options with correct structure
    return searchedMembers
      .filter((option) => {
        // Check if it's a person type
        return option.type === 'person' || option.recordType === 'PERSON' || (!option.type && option.username);
      })
      .map((option) => {
        // Map API response fields to IMemberOption structure
        return {
          ...option,
          type: 'person', // Ensure type is set to 'person'
          role: option.roleName || option.role || '', // Map roleName to role
          name: option.label || option.name || '', // Map label to name
          location: option.location || '', // Ensure location field exists
        };
      });
  }, [searchedMembers]);

  // State for selected members (only IMemberOption)
  const [selectedMembers, setSelectedMembers] = useState([]);

  // Conversation settings state
  const [learnerCanSendMessages, setLearnerCanSendMessages] = useState(
    initialConversationSettings.learnerCanSendMessages ?? true
  );
  const [limitLearnerMessages, setLimitLearnerMessages] = useState(
    initialConversationSettings.limitLearnerMessages ?? true
  );
  const [numberOfMessages, setNumberOfMessages] = useState(
    initialConversationSettings.numberOfMessages ?? 3
  );
  const [shutDownConversation, setShutDownConversation] = useState(
    initialConversationSettings.shutDownConversation ?? false
  );

  // Validation state
  const [memberSelectionError, setMemberSelectionError] = useState('');

  // Check if maximum members allowed is reached
  const isMaxMembersReached = maximumMembersAllowed
    ? selectedMembers.length >= maximumMembersAllowed
    : false;

  useEffect(() => {
    if (isMaxMembersReached) {
      // Could show toast notification here if needed
    }
  }, [isMaxMembersReached]);

  // Inject global styles for footer (matching NewMessageModal approach)
  useEffect(() => {
    const styleId = 'attorney-autocomplete-footer-styles';
    
    // Remove existing style if it exists
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Create and inject new styles
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = getGlobalFooterStyles();
    document.head.appendChild(style);

    // Cleanup function to remove styles when component unmounts
    return () => {
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  // Helper function to apply paper styles
  const applyPaperStyles = (dropdownPaper, optionCount) => {
    if (!dropdownPaper) return;

    // Layout properties
    dropdownPaper.style.display = 'flex';
    dropdownPaper.style.paddingTop = '20px';
    dropdownPaper.style.flexDirection = 'column';
    dropdownPaper.style.alignItems = 'flex-start';
    dropdownPaper.style.alignSelf = 'stretch';

    // Styling properties
    dropdownPaper.style.borderRadius = '24px';
    dropdownPaper.style.background = '#FFF';
    dropdownPaper.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)';
    dropdownPaper.style.position = 'relative';

    // Scrollbar styling (Firefox)
    dropdownPaper.style.scrollbarWidth = 'thin';
    dropdownPaper.style.scrollbarColor = 'var(--Greyscale---grey-60, #CDD4E3) transparent';

    // Height and overflow based on option count
    if (optionCount === 0) {
      // When no options, set minimum height to show footer
      dropdownPaper.style.minHeight = '100px';
      dropdownPaper.style.overflowY = 'hidden';
    } else if (optionCount === 1) {
      dropdownPaper.style.minHeight = '170px';
      dropdownPaper.style.overflowY = 'hidden';
    } else {
      dropdownPaper.style.overflowY = 'auto';
    }

    // Gradient overlay will be added in applyFooterStyles to ensure correct order
  };

  // Helper function to apply listbox styles
  const applyListboxStyles = (listbox) => {
    if (!listbox) return;

    // Width and layout
    listbox.style.width = '100%';
    listbox.style.alignSelf = 'stretch';

    // Padding
    listbox.style.paddingTop = '0px';
    listbox.style.paddingBottom = '50px';
    listbox.style.paddingLeft = '0px';
    listbox.style.paddingRight = '0px';

    // Scrollbar styling (Firefox)
    listbox.style.scrollbarWidth = 'thin';
    listbox.style.scrollbarColor = 'var(--Greyscale---grey-60, #CDD4E3) transparent';

    // Option padding is handled via CSS classes in global styles
  };

  // Helper function to create and style footer
  const applyFooterStyles = (dropdownPaper, memberOptions) => {
    if (!dropdownPaper) return;

    // Always remove existing footer first
    const existingFooter = dropdownPaper.querySelector('.dropdown-footer');
    if (existingFooter) {
      existingFooter.remove();
    }

    // Show footer even when there are no results (to replace "no options" message)

    // Ensure gradient overlay exists before adding footer
    let gradientOverlay = dropdownPaper.querySelector('.dropdown-gradient-overlay');
    if (!gradientOverlay) {
      gradientOverlay = document.createElement('div');
      gradientOverlay.className = 'dropdown-gradient-overlay';
      gradientOverlay.style.cssText = `
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 50px;
        background: linear-gradient(to top, white 0%, rgba(255, 255, 255, 0.8) 50%, transparent 100%);
        pointer-events: none;
        z-index: 1;
      `;
      dropdownPaper.appendChild(gradientOverlay);
    }

    // Create footer container
    const footer = document.createElement('div');
    footer.className = 'dropdown-footer';
    footer.style.position = 'absolute';
    footer.style.bottom = '0';
    footer.style.left = '0';
    footer.style.right = '0';
    footer.style.background = 'white';
    footer.style.display = 'flex';
    footer.style.padding = '24px 20px 12px 20px';
    footer.style.justifyContent = 'flex-end';
    footer.style.alignItems = 'center';
    footer.style.gap = '4px';
    footer.style.alignSelf = 'stretch';
    footer.style.zIndex = '2';

    // Create footer left section
    const footerLeft = document.createElement('div');
    footerLeft.className = 'footer-left';
    footerLeft.textContent = 'Be specific for quicker results';
    footerLeft.style.color = 'var(--success-green-green-dark, #228053)';
    footerLeft.style.fontFamily = 'Inter';
    footerLeft.style.fontSize = '12px';
    footerLeft.style.fontStyle = 'normal';
    footerLeft.style.fontWeight = '400';
    footerLeft.style.lineHeight = '22px';
    footerLeft.style.letterSpacing = '0.24px';
    footerLeft.style.marginRight = 'auto';

    // Create footer right section
    const footerRight = document.createElement('div');
    footerRight.className = 'footer-right';
    const resultCount = memberOptions.length;
    footerRight.textContent = `${resultCount}/${resultCount} Results`;
    footerRight.style.color = 'var(--primary-blue-primary-deep, #0F2C54)';
    footerRight.style.textAlign = 'right';
    footerRight.style.fontFamily = 'Inter';
    footerRight.style.fontSize = '12px';
    footerRight.style.fontStyle = 'normal';
    footerRight.style.fontWeight = '400';
    footerRight.style.lineHeight = '22px';
    footerRight.style.letterSpacing = '0.24px';

    // Assemble footer
    footer.appendChild(footerLeft);
    footer.appendChild(footerRight);
    dropdownPaper.appendChild(footer);
  };

  // Main orchestrator function to setup all dropdown styles
  const setupDropdownStyles = (dropdownPaper, listbox, memberOptions) => {
    if (!dropdownPaper) return;

    const optionCount = memberOptions.length;

    // Apply paper styles (includes gradient overlay)
    applyPaperStyles(dropdownPaper, optionCount);

    // Apply listbox styles if available
    if (listbox) {
      applyListboxStyles(listbox);
    }

    // Apply footer styles (must be after gradient overlay to appear on top)
    applyFooterStyles(dropdownPaper, memberOptions);
  };

  // Dropdown footer effect - similar to useNewMessageModalHook
  useEffect(() => {
    // Get autocomplete root element - defined at the top level of useEffect
    const getAutocompleteRoot = () => {
      return document.querySelector('.attorney-autocomplete-root');
    };

    const addFooterToDropdown = () => {
      // Find the autocomplete paper - look for it within the attorney autocomplete root
      const autocompleteRoot = getAutocompleteRoot();
      if (!autocompleteRoot) return;
      
      const dropdownPaper = autocompleteRoot.querySelector('.MuiAutocomplete-paper') || 
                            document.querySelector('.MuiAutocomplete-paper');
      
      const listbox = dropdownPaper?.querySelector('.MuiAutocomplete-listbox') || null;

      if (dropdownPaper) {
        // Use the orchestrator function to setup all styles
        setupDropdownStyles(dropdownPaper, listbox, memberOptions);
      }
    };

    const updateFooter = () => {
      const autocompleteRoot = getAutocompleteRoot();
      if (!autocompleteRoot) return;
      
      const dropdownPaper = autocompleteRoot.querySelector('.MuiAutocomplete-paper') || 
                            document.querySelector('.MuiAutocomplete-paper');
      
      if (dropdownPaper) {
        // Always use setupDropdownStyles to ensure footer is properly updated or removed
        const listbox = dropdownPaper.querySelector('.MuiAutocomplete-listbox') || null;
        setupDropdownStyles(dropdownPaper, listbox, memberOptions);
      }
    };

    // Use setTimeout to ensure the dropdown is rendered
    const timer = setTimeout(addFooterToDropdown, 100);

    // Also listen for focus events to add footer when dropdown opens
    const handleFocus = () => {
      setTimeout(addFooterToDropdown, 100);
    };

    // Listen for dropdown open events to update footer
    const handleDropdownOpen = () => {
      setTimeout(() => {
        addFooterToDropdown();
        updateFooter();
      }, 100);
    };

    // Get elements for event listeners - use setTimeout to ensure DOM is ready
    let autocompleteRoot = null;
    let inputElement = null;
    let observer = null;

    const setupEventListeners = () => {
      const root = getAutocompleteRoot();
      if (!root) return;
      
      autocompleteRoot = root;
      inputElement = autocompleteRoot.querySelector('.MuiAutocomplete-inputRoot input') ||
                     document.querySelector('.MuiAutocomplete-inputRoot input');
      
      // Add event listeners
      if (inputElement) {
        inputElement.addEventListener('focus', handleFocus);
      }

      // Listen for autocomplete open events
      autocompleteRoot.addEventListener('click', handleDropdownOpen);

      // Use MutationObserver to detect when dropdown opens
      observer = new MutationObserver(() => {
        setTimeout(addFooterToDropdown, 100);
      });

      observer.observe(autocompleteRoot, {
        childList: true,
        subtree: true,
      });
    };

    // Setup event listeners after a short delay to ensure DOM is ready
    const setupTimer = setTimeout(setupEventListeners, 100);

    return () => {
      clearTimeout(timer);
      clearTimeout(setupTimer);
      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus);
      }
      if (autocompleteRoot) {
        autocompleteRoot.removeEventListener('click', handleDropdownOpen);
      }
      if (observer) {
        observer.disconnect();
      }
    };
    // setupDropdownStyles is a stable module-local helper redefined each render;
    // including it would rebind listeners without meaningful behavior change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberOptions]);

  // Handler functions
  const handleRemoveMember = useCallback((memberToRemove) => {
    const updatedMembers = selectedMembers.filter((member) => {
      // Compare by id if both have it
      if (member.id && memberToRemove.id) {
        return member.id !== memberToRemove.id;
      }
      // For person types, compare by username
      if (member.username && memberToRemove.username) {
        return member.username !== memberToRemove.username;
      }
      // Fallback to label/name comparison
      const memberName = member.label || member.name;
      const removeName = memberToRemove.label || memberToRemove.name;
      return memberName !== removeName;
    });
    setSelectedMembers(updatedMembers);
  }, [selectedMembers]);

  const handleMembersChange = useCallback((newValue) => {
    // Prevent selecting more than 2 total members
    if (newValue.length > 2) {
      store.dispatch(changeToastType('error'));
      store.dispatch(showToast('You can only select 1 learner and 1 attorney'));
      return; // Don't update selectedMembers
    }

    // Check if more than 1 learner is selected
    const learners = getLearners(newValue);
    const attorneys = getAttorneys(newValue);

    if (learners.length > 1) {
      store.dispatch(changeToastType('error'));
      store.dispatch(showToast('You can only select 1 learner and 1 attorney'));
      return; // Don't update selectedMembers
    }

    if (attorneys.length > 1) {
      store.dispatch(changeToastType('error'));
      store.dispatch(showToast('You can only select 1 learner and 1 attorney'));
      return; // Don't update selectedMembers
    }

    setSelectedMembers(newValue);
    // Clear error when members are selected correctly
    if (newValue.length > 0 && memberSelectionError) {
      setMemberSelectionError('');
    }
  }, [memberSelectionError]);

  const handleSearchChange = useCallback((searchValue) => {
    if (searchValue.trim() !== '') {
      searchItem(searchValue);
    } else {
      // Clear results when search is empty
      setSearchedMembers([]);
    }
  }, [searchItem]);

  const handleCreateConversation = useCallback(() => {
    // Validate member selection - must have exactly one learner and one attorney
    const validation = validateAttorneyLearnerSelection(selectedMembers);

    if (!validation.isValid) {
      setMemberSelectionError(validation.error);
      if (validation.toastMessage) {
        store.dispatch(changeToastType('error'));
        store.dispatch(showToast(validation.toastMessage));
      }
      return;
    }

    // Clear error if validation passes
    setMemberSelectionError('');

    if (onCreateConversation) {
      const conversationData = {
        selectedMembers: selectedMembers,
        conversationSettings: {
          learnerCanSendMessages,
          limitLearnerMessages,
          numberOfMessages,
          shutDownConversation,
        },
        isAttorneyConversation: true, // Flag to indicate this is an attorney conversation
      };
      onCreateConversation(conversationData);
    }
  }, [
    selectedMembers,
    learnerCanSendMessages,
    limitLearnerMessages,
    numberOfMessages,
    shutDownConversation,
    onCreateConversation,
  ]);

  // Helper functions for autocomplete
  const getOptionLabel = useCallback((option) => {
    if (option.username) {
      // Include docId and label/name in searchable text for better search functionality
      const name = option.label || option.name;
      const searchableText = [option.username, option.docId, name]
        .filter(Boolean)
        .join(' ');
      return searchableText;
    }
    return option.label || option.name || '';
  }, []);

  const filterOptions = useCallback((options) => {
    // No additional filtering needed, already filtered to members only
    return options;
  }, []);

  const isOptionEqualToValue = useCallback((option, value) => {
    if (option.username && value.username) {
      if (
        option.username &&
        value.username &&
        option.username !== '' &&
        value.username !== ''
      ) {
        return option.username === value.username;
      }
    }
    // Fallback to id comparison
    if (option.id && value.id) {
      return option.id === value.id;
    }
    // Fallback to label/name comparison
    const optionName = option.label || option.name;
    const valueName = value.label || value.name;
    return optionName === valueName;
  }, []);

  const getButtonDisabledState = useCallback(() => {
    if (isCreateLoading) {
      return true;
    }

    // Button should be disabled unless exactly one learner and one attorney are selected
    return !isValidSelectionForConversation(selectedMembers);
  }, [selectedMembers, isCreateLoading]);

  return {
    memberOptions,
    selectedMembers,
    learnerCanSendMessages,
    limitLearnerMessages,
    numberOfMessages,
    shutDownConversation,
    isMaxMembersReached,
    memberSelectionError,
    isMemberSearchLoading,
    setLearnerCanSendMessages,
    setLimitLearnerMessages,
    setNumberOfMessages,
    setShutDownConversation,
    handleRemoveMember,
    handleMembersChange,
    handleSearchChange,
    handleCreateConversation,
    getOptionLabel,
    filterOptions,
    isOptionEqualToValue,
    getButtonDisabledState,
  };
};

